/**
 * 收费项目的支付页面
 */

import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { service } from 'service';
import { useRequest, useBrowserTitle, useWxConfig, useLocationQuery, usePayNotice } from 'hook/index';
import { BlockLoading } from 'component/BlockLoading/BlockLoading';
import { bridge, isWechatMiniProgram, openMiniProgramPayLanding, JumpMethod, pageSchema } from 'util/index';
import { VipHero, PackageGrid, MemberBenefits } from 'component/pay/PayParts';
import TrialReport from 'component/pay/TrialReport';
import styles from './Pay.module.scss';

/**
 * 支付守卫的兜底解锁时长（毫秒）。
 *
 * 守卫从点击起一直撑到微信支付面板给回调为止；但 `WeixinJSBridge.invoke` 的回调
 * 在用户切走 App / 面板被系统吞掉时**可能永远不来**，那样按钮就锁死了。
 * 15 秒足够覆盖一次正常的"看到面板 → 输密码 → 回来"，又不会把按钮长期锁死。
 */
const PAY_SUBMIT_UNLOCK_MS = 15000;

interface AllProductMap {
    [k: string]: string;
}

interface BackendData {
    // 技能标志
    type: string;
    appName: string;
    // 技能中文名
    appNameCh: string | null;
    // 包几个月
    months: number;
    // 价格
    price: number;
    discount: number;
    originPrice: number;
}

const allProductMap: AllProductMap = {
    add10:"十以内加法",
    add20:"二十以内加法",
    add100:"一百以内加法",
    divide100:"一百以内除法",
    minus20:"二十以内减法",
    minus100:"一百以内减法",
    ts:"唐诗",
    szj:"三字经",
    by99:"九九乘法表",
    by99backstepping:"逆向九九乘法表",
    mental:"珠心算",
    dzg:"弟子规",
    mixoperator:"四则口算",
    myhomework:"我的作业",
    bigplus:"大数加法",
    myhardadd20:"二十以内加法",
};

// 可购买的产品
const onlineProduct = ['add100', 'mixoperator'];

const APP_PAY_CHANNEL = {
  wechat: 'wechat',
  alipay: 'alipay',
};

export default function Pay() {
    useBrowserTitle('VIP会员服务');
    useWxConfig();
    const history = useHistory();
    const query = useLocationQuery();
    const isApp = bridge.isInApp();
    const {
      checked: payNoticeChecked,
      toggle: togglePayNotice,
      viewVipNotice,
    } = usePayNotice();
    // 支付的来源技能名，有可能从设备扫码带过来
    const skillName = (query.appName || '') as string;
    // 是否来自试用到期召回消息。只有这个来源才显示体验期学习报告——
    // 其它入口（扫码、会员中心、付费墙）进来的家长没有体验期，报告对他们没有意义
    const fromTrialExpire = query.from === 'trial_expire';
    // const [product, setProduct] = useState('');
    // 包几个月
    const [month, setMonth] = useState(() => {
        if (query) {
            return parseInt(query.months as string, 10) || 6;
        } else {
            return 6
        }
    });
    const [isSubmit, setIsSubmit] = useState(false);
    const [appPayChannel, setAppPayChannel] = useState(APP_PAY_CHANNEL.wechat);
    const appPayChannelList = [
      {
        value: APP_PAY_CHANNEL.wechat,
        text: '微信支付',
      },
      {
        value: APP_PAY_CHANNEL.alipay,
        text: '支付宝'
      },
    ];

    const {
        loading,
        data,
    } = useRequest<BackendData[]>({
        url: `/wechat/getproducts?payChannel=wechat&${window.location.search.substring(1)}`,
        method: 'GET',
        defaultData: [],
        defaultIsLoading: true,
    });
    const productList = data || [];
    // 首次请求到产品列表后，设置默认选项
    useEffect(() => {
        if (!productList || productList.length < 1) {
            return;
        }
        if (month) {
            return;
        }
        // setProduct(productList[0].appName);
        setMonth(productList[0].months);
    }, [productList, month]);
    if (loading) {
        return (
            <BlockLoading/>
        );
    }
    const currentProduct = productList.find((obj: any) => { return obj.months === month});
    let price = '--';
    if (currentProduct) {
        price = (currentProduct.price / 100).toFixed(2);
    }
    // 使用微信支付
    function onRequestWechatPay() {
        // ⚠️ 防重复提交。这一条**曾经只有支付宝和 APP 微信两条分支有**（见下方
        // onRequestAlipay / onRequestAppWechatPay），公众号内微信支付这条唯独漏了 ——
        // 于是连点两次就是**两笔真订单、两个 prepay_id**，家长的微信里连弹两次支付面板。
        // 08-19 18:58 线上实证：5353807 相隔 1.1 秒下了两笔 ¥41.95，最终一笔都没付。
        // 服务端也补了幂等（wxPaySkill），两道闸都要在：这里防的是"面板弹两次"的观感，
        // 服务端防的是"订单表里多一条 / 极端情况下重复扣费"。
        //
        // ⚠️⚠️ **守卫必须撑到支付面板关掉，不能在收到响应时就复位。**
        // 08-19 那两次点击相隔 1.1 秒，而下单接口 466ms 就返回了 —— 如果按同文件
        // 其它分支那样在 .then 开头 setIsSubmit(false)，第二次点击照样放行，
        // 这个守卫等于没加。所以复位点放在 invoke 回调 / 失败分支里。
        // 兜底定时器是为了防"用户切走 App，invoke 回调永远不来"把按钮锁死 ——
        // 按钮锁死同样会丢单，比重复下单更难被发现。
        if (isSubmit) {
            return;
        }
        setIsSubmit(true);
        const unlock = () => setIsSubmit(false);
        const unlockTimer = window.setTimeout(unlock, PAY_SUBMIT_UNLOCK_MS);
        const release = () => {
            window.clearTimeout(unlockTimer);
            unlock();
        };
        service.post('/wx/wxPaySkill', {
            serviceType: skillName || currentProduct && currentProduct.appName,
            months: month,
            // uh：bot 侧 uid 的 8 位指纹，由付费码 URL 透传，用于订单与音箱端埋点 join
            uh: query.uh || '',
            // from：来源归因。家长通知里的转化钩子跳本页时带 from=notify，
            // 不透传的话后端 order_create 就分不出"异步触达带来的单"和"二维码带来的单"
            from: query.from || '',
        })
            .then(function(out){
                if (out.status === 200 && out.data) {
                    if (out.data.status === 0) {
                        const data = out.data.data;
                        // alert(JSON.stringify(data));
                        WeixinJSBridge.invoke(
                            'getBrandWCPayRequest', data,
                            function(res: any) {
                                release();
                                const orderNo = (data && data.out_trade_no) || '';
                                /**
                                 * 上报收银台的返回（2026-09-13 加）。
                                 *
                                 * 为什么必须上报：`下单 → 支付` 20 天只有 56%(5/9)，
                                 * 丢掉的 4 个人到底是**家长自己关掉了支付面板**、还是**支付真的坏了**，
                                 * 此前分不开 —— 下面这段把 cancel 和真失败都 alert 成「支付失败」，
                                 * 而服务端只看得到"没收到回调"，两种原因长得一模一样。
                                 * 这两条路要做的事完全相反：cancel 多 = 价格/信任问题，要改文案与定价；
                                 * fail 多 = 路径坏了，修 bug 就能把订单拿回来。
                                 *
                                 * ⚠️ fire-and-forget，且**放在 release() 之后**：
                                 * 上报再怎么失败都不能影响按钮解锁与结果展示。
                                 */
                                service.post('/wx/payResultReport', {
                                    err_msg: res && res.err_msg,
                                    out_trade_no: orderNo,
                                    uh: query.uh || '',
                                    app: skillName || (currentProduct && currentProduct.appName) || '',
                                }).catch(function(){ /* 埋点失败静默 */ });
                                if (res.err_msg === "get_brand_wcpay_request:ok" ){
                                    // 微信提示 err_msg 不绝对可靠，故成功页以服务端开通状态为准展示。
                                    // 不再 alert+closeWindow（瞬时且无可回看确认），改为跳转持久的支付成功页 + 引导关注公众号。
                                    history.replace(`/mp/pay/success?payType=${encodeURIComponent('微信支付')}&money=${encodeURIComponent(price)}&orderNo=${encodeURIComponent(orderNo)}`);
                                } else if (res.err_msg === "get_brand_wcpay_request:cancel") {
                                    /**
                                     * 家长自己关掉了支付面板。**这不是失败，不该报错。**
                                     * 原来它与真失败共用一句「支付失败」—— 一个主动取消的家长
                                     * 被告知"失败"，下次更不敢试；我们也因此永远不知道
                                     * 这两种人各占多少。现在静默，分类交给上面那条埋点。
                                     */
                                    console.log(JSON.stringify(res));
                                } else {
                                    console.log(JSON.stringify(res));
                                    alert('支付失败，请重试或联系客服');
                                }
                            });
                    } else {
                        throw new Error(out.data.message);
                    }
                } else {
                    throw new Error(`网络异常，请稍后再试`);
                }
            })
            .catch(function(err: Error){
                release();
                alert(err.message);
            });
    }

    // 在语音口算APP里，使用支付宝SDK支付
    async function onRequestAlipay() {
      if (isSubmit) {
        return;
      }
      setIsSubmit(true);
      try {
        const res = await service.post('/alipay/createAppPayOrder', {
          serviceType: skillName || currentProduct && currentProduct.appName,
          months: month,
        });
        if (res.status === 200 && res.data && res.data.status === 0) {
          bridge.invokeAlipayForApp(res.data.data.res)
            .then((payResult: any) => {
              console.log('payResult=', payResult);
              // alert('支付结果正常');
              if (payResult.resultStatus === '9000') {
                // 支付成功 → 跳持久成功页（展示开通状态 + 引导关注公众号），不再用瞬时 alert
                let money = '';
                try {
                  const temp = JSON.parse(payResult.result);
                  money = temp.alipay_trade_app_pay_response.total_amount;
                } catch (err) {
                  console.error('alipay parse res money error ', err);
                }
                const orderNo = (res.data.data && res.data.data.out_trade_no) || '';
                history.replace(`/mp/pay/success?payType=${encodeURIComponent('支付宝')}&money=${encodeURIComponent(money)}&orderNo=${encodeURIComponent(orderNo)}`);
              }
            })
            .catch((err) => {
              console.error('onRequestAlipay result error', err);
              alert('支付结果异常：' + err.message);
            });
        } else {
          throw new Error('创建订单异常');
        }
        // alert('支付宝创建订单返回接口\n' + JSON.stringify(res));
      } catch (err) {
        alert(err.message);
      }
      setIsSubmit(false);
    }
  // 在语音口算APP里，使用微信SDK支付
  async function onRequestAppWechatPay() {
    if (isSubmit) {
      return;
    }
    setIsSubmit(true);
    try {
      const res = await service.post('/weixin/createAppPayOrder', {
        serviceType: skillName || currentProduct && currentProduct.appName,
        months: month,
      });
      if (res.status === 200 && res.data && res.data.status === 0) {
        bridge.invokeWechatForApp(JSON.stringify(res.data.data))
          .then((payResult: any) => {
            console.log('payResult=', payResult);
            // alert('支付结果正常');
            if (payResult.errCode === 0) {
              // 支付成功 → 跳持久成功页（展示开通状态 + 引导关注公众号），不再用瞬时 alert
              let money = '';
              try {
                const temp = JSON.parse(payResult.result);
                money = temp.alipay_trade_app_pay_response.total_amount;
              } catch (err) {
                console.error('wechatPay parse res money error ', err);
              }
              const orderNo = (res.data.data && res.data.data.out_trade_no) || '';
              history.replace(`/mp/pay/success?payType=${encodeURIComponent('微信')}&money=${encodeURIComponent(money)}&orderNo=${encodeURIComponent(orderNo)}`);
            }
          })
          .catch((err) => {
            console.error('invokeWechatForApp result error', err);
            alert('支付结果异常：' + err.message);
          });
      } else {
        throw new Error('创建订单异常');
      }
      // alert('支付宝创建订单返回接口\n' + JSON.stringify(res));
    } catch (err) {
      alert(err.message);
    }
    setIsSubmit(false);
  }
    // 支付按钮
    function onClickPay() {
      if (!payNoticeChecked) {
        alert('请先阅读并同意《小学僮会员服务协议》');
        return;
      }
      if (isApp) {
        if (appPayChannel === APP_PAY_CHANNEL.wechat) {
          onRequestAppWechatPay();
        } else {
          onRequestAlipay();
        }
      } else if (isWechatMiniProgram()) {
        // 微信小程序
        openMiniProgramPayLanding({
          serviceType: skillName || (currentProduct && currentProduct.appName) as string,
          months: month,
          jumpMethod: JumpMethod.navigateTo,
          targetUrl: `${pageSchema.miniappTab}/pages/company_my/company_my`,
        });
      } else {
        // 微信公众号
        onRequestWechatPay();
      }
    }
    const payBtnText = price !== '--' ? `支付 ¥${price}` : '支付';
    return (
        <div className={styles.page}>
            {/* 体验期学习报告：只在试用到期召回点进来时出现，且排在套餐之前。
                家长点的是「学习报告提醒」，先看到报告才对得上——直接甩付费墙是标题党。
                拿不到数据时该组件自己返回 null，页面退化成原来的付费页。 */}
            {fromTrialExpire && <TrialReport mid={(query.mid || '') as string} />}

            {/* VIP 权益头卡 */}
            <VipHero />

            {/* 选择套餐 */}
            <PackageGrid productList={productList} month={month} onSelect={setMonth} />

            {/* 支付方式（仅 App 内多渠道时显示） */}
            {
                isApp && (
                    <div className={styles.card}>
                        <div className={styles.secTitle}><span className={styles.secBar} /><span>支付方式</span></div>
                        <div className={styles.channelRow}>
                            {
                                appPayChannelList.map((obj: any) => {
                                    const on = appPayChannel === obj.value;
                                    return (
                                        <div
                                            key={obj.value}
                                            className={`${styles.channelOpt} ${on ? styles.channelOptOn : ''}`}
                                            onClick={() => setAppPayChannel(obj.value)}
                                        >
                                            {obj.text}
                                        </div>
                                    );
                                })
                            }
                        </div>
                    </div>
                )
            }

            {/* 会员权益 */}
            <MemberBenefits />

            {/* 协议 + 支付 */}
            <div className={styles.payWrap}>
                <div className={styles.agree}>
                    <span
                        className={`${styles.agreeBox} ${payNoticeChecked ? styles.agreeBoxOn : ''}`}
                        onClick={togglePayNotice}
                    >✓</span>
                    <span onClick={togglePayNotice}>已阅读并同意</span>
                    <span className={styles.agreeLink} onClick={viewVipNotice}>《小学僮会员服务协议》</span>
                </div>
                <div
                    onClick={onClickPay}
                    className={`${styles.payBtn} ${isSubmit ? styles.payBtnDisabled : ''}`}
                >{payBtnText}</div>
                <div className={styles.note}>虚拟商品不支持退换货，请在购买前确认。</div>
            </div>
        </div>
    );
}
